import React, { useMemo, useRef, useState, useCallback, useEffect, useLayoutEffect } from 'react';
import styled from '@emotion/styled';
import { Box, Typography, Chip, Button, ButtonBase, IconButton, Tooltip, Menu, MenuItem, Divider, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from '@mui/material';
import { Add, MoreVert, ChevronLeft, ChevronRight } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import KanbanCard from './KanbanCard';
import KanbanCardDialog from './KanbanCardDialog';
import ConfirmDialog from './admin/ConfirmDialog';
import { DEFAULT_KANBAN_COLUMNS, DONE_COLUMN_ID, ARCHIVED_COLUMN_ID, STALE_COLUMN_MESSAGE, MISSING_CARD_MESSAGE, isStaleColumnError, isMissingCardError, sameColumns } from '../utils/kanbanColumns';
import { moveBoardCardColumn, removeBoardCard, getBoardCards, updateBoardColumns } from '../redux/ducks/task';
import { showAlert } from '../redux/slices/alerts';
import { toLocalDay } from '../utils/dateUtils';
import { useHasPermission } from '../utils/permissionUtils';
import { useSocketEvent } from '../hooks/useSocketEvent';

const MAX_CUSTOM_COLUMNS = 10;
const MAX_COLUMN_LABEL = 40;
const COLUMNS_BUSY_MESSAGE = 'The last column change is still saving. Try that again in a moment.';
const BOARD_BOTTOM_GUTTER = 8;
const BOARD_MIN_HEIGHT = 320;

const BoardContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(2),
    overflowX: 'auto',
    overflowY: 'hidden',
    paddingBottom: theme.spacing(2),
    alignItems: 'stretch',
    height: 'calc(100vh - var(--kanban-board-offset, 40vh))',
    '@supports (height: 100dvh)': {
        height: 'calc(100dvh - var(--kanban-board-offset, 40vh))',
    },
    minHeight: BOARD_MIN_HEIGHT,
    width: '100%',
}));

const Column = styled(Box, { shouldForwardProp: (prop) => prop !== 'dragOver' && prop !== 'pending' })(({ theme, dragOver, pending }) => ({
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 0',
    minWidth: 260,
    maxWidth: 360,
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${dragOver ? theme.palette.primary.main : theme.palette.divider}`,
    boxShadow: dragOver ? `0 0 0 2px ${theme.palette.primary.light} inset` : 'none',
    opacity: pending ? 0.6 : 1,
    transition: 'border-color 120ms ease, box-shadow 120ms ease, opacity 120ms ease',
}));

const CollapsedColumn = styled(ButtonBase)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: theme.spacing(1),
    flex: '0 0 auto',
    width: 52,
    alignSelf: 'stretch',
    padding: theme.spacing(1.5, 0),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.secondary,
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
    },
}));

const CollapsedLabel = styled(Typography)(({ theme }) => ({
    writingMode: 'vertical-rl',
    fontWeight: theme.typography.fontWeightBold,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    fontSize: theme.typography.caption.fontSize,
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
}));

const ColumnHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1.25, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    position: 'sticky',
    top: 0,
    backgroundColor: theme.palette.background.paper,
    borderTopLeftRadius: theme.shape.borderRadius,
    borderTopRightRadius: theme.shape.borderRadius,
    zIndex: 1,
}));

const ColumnTitle = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightBold,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    fontSize: theme.typography.caption.fontSize,
    color: theme.palette.text.secondary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
}));

const ColumnHeaderActions = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    flexShrink: 0,
}));

const CardList = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5),
    flexGrow: 1,
    overflowY: 'auto',
    minHeight: theme.spacing(20),
}));

const CardSlot = styled(Box, { shouldForwardProp: (prop) => prop !== 'dragging' })(({ dragging }) => ({
    opacity: dragging ? 0.5 : 1,
}));

const EmptyHint = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.disabled,
    fontStyle: 'italic',
    textAlign: 'center',
    padding: theme.spacing(2),
    fontSize: theme.typography.caption.fontSize,
}));

const ColumnFooter = styled(Box)(({ theme }) => ({
    padding: theme.spacing(1, 1.5, 1.5),
    borderTop: `1px dashed ${theme.palette.divider}`,
}));

const AddCardButton = styled(Button)(({ theme }) => ({
    width: '100%',
    justifyContent: 'flex-start',
    textTransform: 'none',
    color: theme.palette.text.secondary,
    fontSize: theme.typography.caption.fontSize,
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
    },
}));

const AddColumnButton = styled(Button)(({ theme }) => ({
    flex: '0 0 auto',
    alignSelf: 'flex-start',
    minWidth: 160,
    textTransform: 'none',
    color: theme.palette.text.secondary,
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1.25, 1.5),
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
        borderStyle: 'dashed',
    },
}));

const DialogHelper = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontSize: theme.typography.caption.fontSize,
    marginBottom: theme.spacing(2),
}));

const useBoardOffset = (ref) => {
    const [offset, setOffset] = useState(null);

    useLayoutEffect(() => {
        const node = ref.current;
        if (!node) return undefined;

        const measure = () => {
            let next = node.getBoundingClientRect().top + window.scrollY + BOARD_BOTTOM_GUTTER;
            for (let parent = node.parentElement; parent && parent !== document.body; parent = parent.parentElement) {
                next += parseFloat(window.getComputedStyle(parent).paddingBottom) || 0;
            }
            setOffset((cur) => (cur !== null && Math.abs(cur - next) < 1 ? cur : next));
        };

        measure();
        window.addEventListener('resize', measure);
        let observer = null;
        if (typeof ResizeObserver !== 'undefined') {
            observer = new ResizeObserver(measure);
            observer.observe(document.body);
        }
        return () => {
            window.removeEventListener('resize', measure);
            if (observer) observer.disconnect();
        };
    }, [ref]);

    return offset;
};

const groupByColumn = (items, columns, previous) => {
    const groups = {};
    const columnIds = new Set();
    for (const col of columns) {
        groups[col.id] = [];
        columnIds.add(col.id);
    }
    const fallback = columns[0].id;
    for (const it of items) {
        const col = it && columnIds.has(it.columnId) ? it.columnId : fallback;
        groups[col].push(it);
    }
    if (previous) {
        for (const id of columnIds) {
            const before = previous[id];
            const after = groups[id];
            if (before && before.length === after.length && before.every((it, index) => it === after[index])) {
                groups[id] = before;
            }
        }
    }
    return groups;
};

const withDoneLast = (customColumns) => [
    ...customColumns.map((col, index) => ({ ...col, order: index })),
    { id: DONE_COLUMN_ID, label: 'Done', order: customColumns.length },
];

const BoardColumn = React.memo(({ col, items, taskId, isDragOver, draggingId, canEdit, columnsLocked, canCompleteBoardTask, canDeleteCard, isPending, isArchived, isCollapsed, onToggleCollapse, onDragOver, onDragLeave, onDrop, onDragStart, onDragEnd, onOpenMenu, onOpenCard, onDeleteCard, onAddCard }) => {
    const columnId = col.id;
    const handleDragOver = useCallback((e) => onDragOver(e, columnId), [onDragOver, columnId]);
    const handleDragLeave = useCallback((e) => onDragLeave(e, columnId), [onDragLeave, columnId]);
    const handleDrop = useCallback((e) => onDrop(e, columnId), [onDrop, columnId]);
    const handleOpenMenu = useCallback((e) => onOpenMenu(e, columnId), [onOpenMenu, columnId]);
    const handleAddCard = useCallback(() => onAddCard(columnId), [onAddCard, columnId]);
    const acceptsDrop = !isPending && !isArchived;
    const canMoveCards = canEdit && !isArchived && (columnId !== DONE_COLUMN_ID || canCompleteBoardTask);

    if (isArchived && isCollapsed) {
        return (
            <CollapsedColumn aria-label={`Expand the ${col.label} column`} aria-expanded={false} onClick={onToggleCollapse}>
                <ChevronLeft fontSize="small" />
                <Chip size="small" label={items.length} />
                <CollapsedLabel variant="caption">{col.label}</CollapsedLabel>
            </CollapsedColumn>
        );
    }

    return (
        <Column
            dragOver={isDragOver}
            pending={isPending}
            onDragOver={acceptsDrop ? handleDragOver : undefined}
            onDragLeave={acceptsDrop ? handleDragLeave : undefined}
            onDrop={acceptsDrop ? handleDrop : undefined}
        >
            <ColumnHeader>
                <ColumnTitle variant="caption">{col.label}</ColumnTitle>
                <ColumnHeaderActions>
                    <Chip size="small" label={items.length} />
                    {isArchived && (
                        <Tooltip title="Collapse column">
                            <IconButton size="small" aria-label={`Collapse the ${col.label} column`} onClick={onToggleCollapse}>
                                <ChevronRight fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                    {canEdit && !columnsLocked && !isPending && !isArchived && columnId !== DONE_COLUMN_ID && (
                        <Tooltip title="Column options">
                            <IconButton size="small" aria-label={`Options for the ${col.label} column`} onClick={handleOpenMenu}>
                                <MoreVert fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                </ColumnHeaderActions>
            </ColumnHeader>
            <CardList>
                {items.length === 0 && <EmptyHint>{acceptsDrop ? 'Drop cards here' : 'No cards'}</EmptyHint>}
                {items.map((item) => (
                    <CardSlot
                        key={item._id}
                        draggable={canMoveCards}
                        dragging={draggingId === item._id}
                        onDragStart={(e) => onDragStart(e, item)}
                        onDragEnd={onDragEnd}
                    >
                        <KanbanCard
                            item={item}
                            taskId={taskId}
                            isLocked={!canEdit}
                            canDrag={canMoveCards}
                            canDelete={canDeleteCard(item)}
                            onOpen={onOpenCard}
                            onDelete={onDeleteCard}
                        />
                    </CardSlot>
                ))}
            </CardList>
            {canMoveCards && !isPending && (
                <ColumnFooter>
                    <AddCardButton size="small" startIcon={<Add fontSize="small" />} onClick={handleAddCard}>
                        Add card
                    </AddCardButton>
                </ColumnFooter>
            )}
        </Column>
    );
});

const KanbanBoard = ({ cards = [], setCards, taskId, columns = DEFAULT_KANBAN_COLUMNS, columnsLocked = false, onColumnsChanged, onRefresh, canEdit = true, canCompleteBoardTask = false, isLocked = false, initialCardId = null }) => {
    const dispatch = useDispatch();
    const hasPermission = useHasPermission();
    const currentUserId = useSelector((state) => state.auth.userId);
    const canEditQATask = hasPermission('editQATask');
    const canSeeArchived = hasPermission('canSeeDeleted');
    const boardRef = useRef(null);
    const boardOffset = useBoardOffset(boardRef);
    const columnsBusyRef = useRef(false);
    const baseColumnsRef = useRef(null);
    const cardsRef = useRef(cards);
    const groupedRef = useRef(null);
    const [dragOver, setDragOver] = useState(null);
    const [draggingId, setDraggingId] = useState(null);
    const [openCardId, setOpenCardId] = useState(null);
    const [creatingInColumn, setCreatingInColumn] = useState(null);
    const [pendingDeleteId, setPendingDeleteId] = useState(null);
    const [pendingColumns, setPendingColumns] = useState(null);
    const [columnsBusy, setColumnsBusy] = useState(false);
    const [menuColumnId, setMenuColumnId] = useState(null);
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [columnDialog, setColumnDialog] = useState(null);
    const [columnName, setColumnName] = useState('');
    const [pendingDeleteColumnId, setPendingDeleteColumnId] = useState(null);
    const [confirmingReset, setConfirmingReset] = useState(false);
    const [archivedExpanded, setArchivedExpanded] = useState(false);

    const effectiveColumns = pendingColumns || columns;
    const visibleColumns = useMemo(
        () => (canSeeArchived ? effectiveColumns : effectiveColumns.filter((col) => col.id !== ARCHIVED_COLUMN_ID)),
        [effectiveColumns, canSeeArchived],
    );
    const customColumns = useMemo(() => effectiveColumns.filter((col) => col.id !== DONE_COLUMN_ID && col.id !== ARCHIVED_COLUMN_ID), [effectiveColumns]);
    const isStandardBoard = useMemo(() => sameColumns(effectiveColumns, DEFAULT_KANBAN_COLUMNS), [effectiveColumns]);

    // Deep link: open a card when the page is reached with ?card= (e.g. from a notification).
    useEffect(() => {
        if (initialCardId && cards.some((c) => c._id === initialCardId)) {
            setOpenCardId(initialCardId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialCardId, cards.length]);

    cardsRef.current = cards;

    const patchCardPushState = useCallback((cardId, card) => {
        if (!cardId || !card) return;
        setCards((cur) => cur.map((it) => (String(it._id) === String(cardId) ? { ...it, ...card } : it)));
    }, [setCards]);

    useSocketEvent('boardCard:pushState', useCallback((payload) => {
        if (String(payload?.boardId) !== String(taskId)) return;
        patchCardPushState(payload.cardId, payload.card);
    }, [taskId, patchCardPushState]));

    const patchCommentPushState = useCallback((cardId, comment) => {
        if (!cardId || !comment?._id) return;
        setCards((cur) => cur.map((it) => (String(it._id) === String(cardId)
            ? { ...it, comments: (it.comments || []).map((c) => (String(c._id) === String(comment._id) ? { ...c, ...comment } : c)) }
            : it)));
    }, [setCards]);

    useSocketEvent('boardCardComment:pushState', useCallback((payload) => {
        patchCommentPushState(payload?.cardId, payload?.comment);
    }, [patchCommentPushState]));

    const grouped = useMemo(() => {
        const next = groupByColumn(cards, effectiveColumns, groupedRef.current);
        groupedRef.current = next;
        return next;
    }, [cards, effectiveColumns]);

    const canDeleteCard = useCallback((item) => {
        if (!canEdit || !item) return false;
        if (item.columnId === DONE_COLUMN_ID && !canCompleteBoardTask) return false;
        const creatorId = item.createdBy && (item.createdBy._id || item.createdBy);
        const isCreator = !!creatorId && !!currentUserId && String(creatorId) === String(currentUserId);
        return isCreator || canEditQATask;
    }, [canEdit, canCompleteBoardTask, canEditQATask, currentUserId]);

    const handleDragStart = useCallback((e, item) => {
        if (!canEdit) return;
        if (item.columnId === DONE_COLUMN_ID && !canCompleteBoardTask) {
            e.preventDefault();
            return;
        }
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', item._id);
        setDraggingId(item._id);
    }, [canEdit, canCompleteBoardTask]);

    const handleDragEnd = useCallback(() => {
        setDraggingId(null);
        setDragOver(null);
    }, []);

    const handleDragOver = useCallback((e, columnId) => {
        if (!canEdit) return;
        if (columnId === DONE_COLUMN_ID && !canCompleteBoardTask) {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'none';
            return;
        }
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDragOver((cur) => (cur === columnId ? cur : columnId));
    }, [canEdit, canCompleteBoardTask]);

    const handleDragLeave = useCallback((e, columnId) => {
        if (e.currentTarget !== e.target) return;
        setDragOver((cur) => (cur === columnId ? null : cur));
    }, []);

    const handleDrop = useCallback((e, columnId) => {
        if (!canEdit) return;
        e.preventDefault();
        const cardId = e.dataTransfer.getData('text/plain') || draggingId;
        setDragOver(null);
        setDraggingId(null);
        if (!cardId) return;
        const current = cardsRef.current.find((it) => it._id === cardId);
        if (!current) return;
        if (current.columnId === columnId) return;
        if (current.columnId === ARCHIVED_COLUMN_ID || columnId === ARCHIVED_COLUMN_ID) return;

        if ((current.columnId === DONE_COLUMN_ID || columnId === DONE_COLUMN_ID) && !canCompleteBoardTask) {
            dispatch(showAlert({ severity: 'warning', message: 'You do not have permission to complete or uncomplete board cards' }));
            return;
        }

        const prevColumnId = current.columnId;
        const prevCompleted = current.completed;
        setCards((cur) => cur.map((it) => it._id === cardId ? {
            ...it,
            columnId,
            completed: columnId === DONE_COLUMN_ID,
            completedAt: columnId === DONE_COLUMN_ID ? (it.completedAt || toLocalDay()) : null,
        } : it));

        dispatch(moveBoardCardColumn(taskId, cardId, columnId, (data, error) => {
            if (error || !data?.cards) {
                setCards((cur) => cur.map((it) => it._id === cardId ? { ...it, columnId: prevColumnId, completed: prevCompleted } : it));
                if (isStaleColumnError(error)) {
                    dispatch(showAlert({ severity: 'warning', message: STALE_COLUMN_MESSAGE }));
                    if (onRefresh) onRefresh();
                } else if (isMissingCardError(error)) {
                    dispatch(showAlert({ severity: 'warning', message: MISSING_CARD_MESSAGE }));
                    if (onRefresh) onRefresh();
                } else if (!error) {
                    dispatch(showAlert({ severity: 'error', message: 'Failed to move card' }));
                }
                return;
            }
            setCards(data.cards);
        }));
    }, [canEdit, canCompleteBoardTask, draggingId, dispatch, taskId, setCards, onRefresh]);

    const handleToggleArchived = useCallback(() => {
        setArchivedExpanded((cur) => !cur);
    }, []);

    const openCreateDialog = useCallback((columnId) => {
        if (!canEdit) return;
        if (columnId === ARCHIVED_COLUMN_ID) return;
        if (columnId === DONE_COLUMN_ID && !canCompleteBoardTask) return;
        setCreatingInColumn(columnId);
    }, [canEdit, canCompleteBoardTask]);

    const handleOpenCard = useCallback((cardItem) => {
        if (!cardItem || !cardItem._id) return;
        setOpenCardId(cardItem._id);
    }, []);

    const handleCloseCardDialog = useCallback(() => {
        setOpenCardId(null);
    }, []);

    const handleCloseCreateDialog = useCallback(() => {
        setCreatingInColumn(null);
    }, []);

    const handleMissingCard = useCallback(() => {
        setOpenCardId(null);
        dispatch(showAlert({ severity: 'warning', message: MISSING_CARD_MESSAGE }));
        if (onRefresh) onRefresh();
    }, [dispatch, onRefresh]);

    const handleStaleColumn = useCallback(() => {
        setCreatingInColumn(null);
        if (onRefresh) onRefresh();
    }, [onRefresh]);

    const handleCardsReplaced = useCallback((nextCards) => {
        if (!Array.isArray(nextCards)) return;
        setCards(nextCards);
    }, [setCards]);

    const handleCardCommentsChanged = useCallback((cardId, nextComments) => {
        setCards((prev) => prev.map((c) => (c._id === cardId ? { ...c, comments: nextComments } : c)));
    }, [setCards]);

    const handleCardCreated = useCallback((nextCards) => {
        if (Array.isArray(nextCards)) setCards(nextCards);
        setCreatingInColumn(null);
    }, [setCards]);

    const handleRequestDelete = useCallback((cardId) => {
        if (!canDeleteCard(cardsRef.current.find((it) => it._id === cardId))) return;
        setPendingDeleteId(cardId);
    }, [canDeleteCard]);

    const handleCancelDelete = useCallback(() => {
        setPendingDeleteId(null);
    }, []);

    const handleConfirmDelete = useCallback(() => {
        const cardId = pendingDeleteId;
        setPendingDeleteId(null);
        if (!cardId || !canDeleteCard(cardsRef.current.find((it) => it._id === cardId))) return;
        setCards((cur) => cur.filter((it) => it._id !== cardId));

        dispatch(removeBoardCard(taskId, cardId, (data, error) => {
            if (error || !data?.cards) {
                if (isMissingCardError(error)) {
                    dispatch(showAlert({ severity: 'warning', message: MISSING_CARD_MESSAGE }));
                    if (onRefresh) onRefresh();
                    return;
                }
                dispatch(getBoardCards(taskId, (fresh, fetchError) => {
                    if (fetchError || !fresh?.cards) return;
                    setCards(fresh.cards);
                }));
                return;
            }
            setCards(data.cards);
        }));
    }, [pendingDeleteId, canDeleteCard, dispatch, taskId, setCards, onRefresh]);

    const submitColumns = useCallback((nextCustomColumns, cardsUpdater) => {
        if (columnsBusyRef.current) {
            dispatch(showAlert({ severity: 'info', message: COLUMNS_BUSY_MESSAGE }));
            return false;
        }
        const previousCards = cards;
        const baseColumns = baseColumnsRef.current || columns.map((col) => ({ id: col.id, label: col.label }));

        columnsBusyRef.current = true;
        setColumnsBusy(true);
        setPendingColumns(nextCustomColumns.length > 0 ? withDoneLast(nextCustomColumns) : DEFAULT_KANBAN_COLUMNS);
        if (cardsUpdater) setCards(cardsUpdater);

        const payload = nextCustomColumns.map((col) => (col.isNew ? { label: col.label } : { id: col.id, label: col.label }));

        dispatch(updateBoardColumns(taskId, payload, baseColumns, (data, error) => {
            columnsBusyRef.current = false;
            setColumnsBusy(false);
            setPendingColumns(null);
            if (error || !data?.columns) {
                if (cardsUpdater) setCards(previousCards);
                if (error?.response?.status === 409) {
                    const serverColumns = error.response.data?.columns;
                    baseColumnsRef.current = Array.isArray(serverColumns) && serverColumns.length > 0
                        ? serverColumns.map((col) => ({ id: col.id, label: col.label }))
                        : null;
                    dispatch(showAlert({ severity: 'warning', message: 'This board changed since it was loaded. Loading the latest version.' }));
                } else if (!error) {
                    dispatch(showAlert({ severity: 'error', message: 'Failed to update the board columns' }));
                }
                if (onRefresh) onRefresh();
                return;
            }
            baseColumnsRef.current = null;
            const movedCardIds = Array.isArray(data.movedCardIds) ? data.movedCardIds.map(String) : [];
            if (movedCardIds.length > 0) {
                const moved = new Set(movedCardIds);
                const landingColumnId = data.columns[0].id;
                setCards((cur) => cur.map((it) => (moved.has(String(it._id))
                    ? { ...it, columnId: landingColumnId, completed: false, completedAt: null }
                    : it)));
            }
            if (onColumnsChanged) onColumnsChanged(data.columns);
        }));
        return true;
    }, [cards, columns, dispatch, taskId, setCards, onColumnsChanged, onRefresh]);

    const handleOpenColumnMenu = useCallback((e, columnId) => {
        setMenuAnchorEl(e.currentTarget);
        setMenuColumnId(columnId);
    }, []);

    const handleCloseColumnMenu = useCallback(() => {
        setMenuAnchorEl(null);
        setMenuColumnId(null);
    }, []);

    const handleOpenAddColumn = useCallback(() => {
        setColumnName('');
        setColumnDialog({ mode: 'add' });
    }, []);

    const handleOpenRenameColumn = useCallback(() => {
        const target = customColumns.find((col) => col.id === menuColumnId);
        if (target) {
            setColumnName(target.label);
            setColumnDialog({ mode: 'rename', id: target.id });
        }
        handleCloseColumnMenu();
    }, [customColumns, menuColumnId, handleCloseColumnMenu]);

    const handleCloseColumnDialog = useCallback(() => {
        setColumnDialog(null);
        setColumnName('');
    }, []);

    const trimmedColumnName = columnName.trim();
    const columnNameError = useMemo(() => {
        if (!trimmedColumnName) return '';
        const comparable = trimmedColumnName.toLowerCase();
        if (comparable === 'done') return '"Done" is reserved for the last column';
        const clash = customColumns.some((col) => col.id !== columnDialog?.id && col.label.trim().toLowerCase() === comparable);
        return clash ? 'Another column already has this name' : '';
    }, [trimmedColumnName, customColumns, columnDialog]);

    const handleSubmitColumnDialog = useCallback(() => {
        const label = columnName.trim();
        if (!label || columnNameError || !columnDialog) return;
        const submitted = columnDialog.mode === 'add'
            ? submitColumns([...customColumns, { id: `new-${Date.now()}`, label, isNew: true }])
            : submitColumns(customColumns.map((col) => (col.id === columnDialog.id ? { ...col, label } : col)));
        if (submitted) handleCloseColumnDialog();
    }, [columnName, columnNameError, columnDialog, customColumns, submitColumns, handleCloseColumnDialog]);

    const handleMoveColumn = useCallback((offset) => {
        const index = customColumns.findIndex((col) => col.id === menuColumnId);
        const target = index + offset;
        handleCloseColumnMenu();
        if (index < 0 || target < 0 || target >= customColumns.length) return;
        const next = [...customColumns];
        next.splice(target, 0, next.splice(index, 1)[0]);
        submitColumns(next);
    }, [customColumns, menuColumnId, submitColumns, handleCloseColumnMenu]);

    const handleRequestDeleteColumn = useCallback(() => {
        setPendingDeleteColumnId(menuColumnId);
        handleCloseColumnMenu();
    }, [menuColumnId, handleCloseColumnMenu]);

    const handleConfirmDeleteColumn = useCallback(() => {
        const columnId = pendingDeleteColumnId;
        setPendingDeleteColumnId(null);
        if (!columnId) return;
        const next = customColumns.filter((col) => col.id !== columnId);
        if (next.length === 0) return;
        const fallbackId = next[0].id;
        submitColumns(next, (cur) => cur.map((it) => (it.columnId === columnId ? { ...it, columnId: fallbackId } : it)));
    }, [pendingDeleteColumnId, customColumns, submitColumns]);

    const handleRequestReset = useCallback(() => {
        setConfirmingReset(true);
        handleCloseColumnMenu();
    }, [handleCloseColumnMenu]);

    const handleConfirmReset = useCallback(() => {
        setConfirmingReset(false);
        submitColumns([]);
    }, [submitColumns]);

    const pendingDeleteCard = pendingDeleteId ? cards.find((it) => it._id === pendingDeleteId) : null;
    const pendingDeleteColumn = pendingDeleteColumnId ? customColumns.find((col) => col.id === pendingDeleteColumnId) : null;
    const menuColumnIndex = menuColumnId ? customColumns.findIndex((col) => col.id === menuColumnId) : -1;
    const columnMenuItems = [
        <MenuItem key="rename" disabled={columnsBusy} onClick={handleOpenRenameColumn}>Rename</MenuItem>,
        <MenuItem key="left" disabled={columnsBusy || menuColumnIndex <= 0} onClick={() => handleMoveColumn(-1)}>Move left</MenuItem>,
        <MenuItem key="right" disabled={columnsBusy || menuColumnIndex < 0 || menuColumnIndex >= customColumns.length - 1} onClick={() => handleMoveColumn(1)}>Move right</MenuItem>,
        <MenuItem key="delete" disabled={columnsBusy || customColumns.length <= 1} onClick={handleRequestDeleteColumn}>Delete</MenuItem>,
        <Divider key="divider" />,
        <MenuItem key="reset" disabled={columnsBusy || isStandardBoard} onClick={handleRequestReset}>Reset to the standard columns</MenuItem>,
    ];

    return (
        <BoardContainer ref={boardRef} style={boardOffset ? { '--kanban-board-offset': `${boardOffset}px` } : undefined}>
            {visibleColumns.map((col) => (
                <BoardColumn
                    key={col.id}
                    col={col}
                    items={grouped[col.id] || []}
                    taskId={taskId}
                    isDragOver={dragOver === col.id}
                    draggingId={draggingId}
                    canEdit={canEdit}
                    columnsLocked={columnsLocked}
                    canCompleteBoardTask={canCompleteBoardTask}
                    canDeleteCard={canDeleteCard}
                    isPending={!!col.isNew}
                    isArchived={col.id === ARCHIVED_COLUMN_ID}
                    isCollapsed={!archivedExpanded}
                    onToggleCollapse={handleToggleArchived}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    onOpenMenu={handleOpenColumnMenu}
                    onOpenCard={handleOpenCard}
                    onDeleteCard={handleRequestDelete}
                    onAddCard={openCreateDialog}
                />
            ))}
            {canEdit && !columnsLocked && customColumns.length < MAX_CUSTOM_COLUMNS && (
                <AddColumnButton size="small" startIcon={<Add fontSize="small" />} disabled={columnsBusy} onClick={handleOpenAddColumn}>
                    Add column
                </AddColumnButton>
            )}
            <Menu anchorEl={menuAnchorEl} open={!!menuAnchorEl} onClose={handleCloseColumnMenu}>
                {columnMenuItems}
            </Menu>
            <Dialog open={!!columnDialog} onClose={handleCloseColumnDialog} maxWidth="xs" fullWidth>
                <DialogTitle>{columnDialog?.mode === 'rename' ? 'Rename column' : 'Add a column'}</DialogTitle>
                <DialogContent>
                    <DialogHelper>
                        {columnDialog?.mode === 'rename'
                            ? 'Cards in this column stay where they are.'
                            : 'The new column is added just before Done.'}
                    </DialogHelper>
                    <TextField
                        autoFocus
                        fullWidth
                        size="small"
                        label="Column name"
                        value={columnName}
                        error={!!columnNameError}
                        helperText={columnNameError}
                        inputProps={{ maxLength: MAX_COLUMN_LABEL }}
                        onChange={(e) => setColumnName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); handleSubmitColumnDialog(); } }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseColumnDialog}>Cancel</Button>
                    <Button variant="contained" disabled={!trimmedColumnName || !!columnNameError || columnsBusy} onClick={handleSubmitColumnDialog}>
                        {columnDialog?.mode === 'rename' ? 'Save name' : 'Add column'}
                    </Button>
                </DialogActions>
            </Dialog>
            <KanbanCardDialog
                open={!!openCardId}
                taskId={taskId}
                item={openCardId ? cards.find((it) => it._id === openCardId) : null}
                canEdit={canEdit}
                isLocked={isLocked}
                onClose={handleCloseCardDialog}
                onCardsReplaced={handleCardsReplaced}
                onCardCommentsChanged={handleCardCommentsChanged}
                onMissingCard={handleMissingCard}
                onCardPushState={patchCardPushState}
                onCommentPushState={patchCommentPushState}
            />
            <KanbanCardDialog
                open={!!creatingInColumn}
                taskId={taskId}
                item={null}
                columnId={creatingInColumn}
                canEdit={canEdit}
                isLocked={isLocked}
                onClose={handleCloseCreateDialog}
                onCardsReplaced={handleCardCreated}
                onStaleColumn={handleStaleColumn}
            />
            <ConfirmDialog
                open={!!pendingDeleteId}
                onClose={handleCancelDelete}
                onConfirm={handleConfirmDelete}
                title="Delete card?"
                message={`"${pendingDeleteCard?.name || 'This card'}" and all of its comments and attachments will be removed. This can't be undone.`}
                confirmLabel="Delete card"
                confirmColor="error"
            />
            <ConfirmDialog
                open={!!pendingDeleteColumnId}
                onClose={() => setPendingDeleteColumnId(null)}
                onConfirm={handleConfirmDeleteColumn}
                title="Delete column?"
                message={`"${pendingDeleteColumn?.label || 'This column'}" will be removed and any cards still in it move to "${customColumns.filter((col) => col.id !== pendingDeleteColumnId)[0]?.label || 'the first column'}".`}
                confirmLabel="Delete column"
                confirmColor="error"
            />
            <ConfirmDialog
                open={confirmingReset}
                onClose={() => setConfirmingReset(false)}
                onConfirm={handleConfirmReset}
                title="Reset to the standard columns?"
                message={`The board goes back to ${DEFAULT_KANBAN_COLUMNS.map((col) => col.label).join(', ')}. Cards in columns that disappear move to "${DEFAULT_KANBAN_COLUMNS[0].label}".`}
                confirmLabel="Reset columns"
            />
        </BoardContainer>
    );
};

export default KanbanBoard;
